<template>
  <div>
    <div class="adm-stats">
      <button v-for="card in cards" :key="card.label" type="button" class="adm-card adm-stat" @click="$emit('navigate', card.panel, card.intent)">
        <span class="adm-stat__icon" :class="card.tone"><i :class="card.icon"></i></span>
        <span>
          <span class="adm-stat__label">{{ card.label }}</span>
          <span class="adm-stat__value">
            <span v-if="card.value === null" class="adm-skeleton adm-stat__skeleton"></span>
            <template v-else>{{ card.value }}</template>
          </span>
        </span>
      </button>
    </div>

    <div class="adm-overview-grid">
      <section class="adm-card">
        <div class="adm-card__header">
          <div>
            <h3 class="adm-card__title">Waiting for approval</h3>
            <p class="adm-card__hint">Newest doctor registrations. Approve to assign a clinic and send their login.</p>
          </div>
          <div class="adm-toolbar__spacer"></div>
          <button type="button" class="adm-btn adm-btn--sm" @click="$emit('navigate', 'doctors', { status: 'pending' })">View all</button>
        </div>
        <div v-if="pendingLoading">
          <div v-for="n in 3" :key="n" class="adm-list-row"><div class="adm-skeleton" style="width: 50%"></div></div>
        </div>
        <div v-else-if="!pending.length" class="adm-empty">
          <i class="fas fa-check-circle"></i>
          <strong>All caught up</strong>
          No doctor registrations are waiting.
        </div>
        <div v-else>
          <div v-for="doctor in pending" :key="doctor.doctorid" class="adm-list-row">
            <span class="adm-avatar">{{ initials(doctor.doctorname) }}</span>
            <div class="adm-overview-person">
              <span class="adm-person__name">{{ doctor.doctorname }}</span>
              <span class="adm-person__meta">{{ doctor.specialization || "Specialization not set" }} · {{ doctor.area || "No city" }}</span>
            </div>
            <button type="button" class="adm-btn adm-btn--sm adm-overview-review" @click="$emit('navigate', 'doctors', { status: 'pending' })">Review</button>
            <button type="button" class="adm-btn adm-btn--sm adm-btn--success" @click="approving = doctor">
              <i class="fas fa-check"></i> Approve
            </button>
          </div>
        </div>
      </section>

      <div class="adm-overview-side">
        <section class="adm-card">
          <div class="adm-card__header"><h3 class="adm-card__title">Quick actions</h3></div>
          <div class="adm-card__body adm-quick">
            <button type="button" class="adm-btn adm-btn--block" @click="$emit('navigate', 'clinics', { create: true })">
              <i class="fas fa-hospital"></i> Add clinic
            </button>
            <button type="button" class="adm-btn adm-btn--block" @click="$emit('navigate', 'clinic-users', { create: true })">
              <i class="fas fa-id-badge"></i> Add clinic login
            </button>
            <button type="button" class="adm-btn adm-btn--block" @click="$emit('navigate', 'admins', { create: true })">
              <i class="fas fa-user-shield"></i> Add admin
            </button>
            <button type="button" class="adm-btn adm-btn--block" @click="$emit('navigate', 'slots')">
              <i class="fas fa-calendar-plus"></i> Generate slots
            </button>
          </div>
        </section>

        <section class="adm-card">
          <div class="adm-card__header">
            <div>
              <h3 class="adm-card__title">Clinics to set up</h3>
              <p class="adm-card__hint">Missing doctors or a staff login.</p>
            </div>
          </div>
          <div v-if="clinics === null">
            <div v-for="n in 2" :key="n" class="adm-list-row"><div class="adm-skeleton" style="width: 60%"></div></div>
          </div>
          <div v-else-if="!clinicsToSetUp.length" class="adm-empty adm-overview-empty">
            <i class="fas fa-check-circle"></i>Every clinic has doctors and a staff login.
          </div>
          <div v-else>
            <div v-for="clinic in clinicsToSetUp.slice(0, 5)" :key="clinic.clinicid" class="adm-list-row">
              <div class="adm-overview-person">
                <span class="adm-person__name">{{ clinic.clinicname }}</span>
                <span class="adm-person__meta">{{ clinic.doctor_count ? "No staff login" : "No doctors assigned" }}</span>
              </div>
              <button type="button" class="adm-btn adm-btn--sm"
                      @click="clinic.doctor_count ? $emit('navigate', 'clinic-users', { create: clinic.clinicid }) : $emit('navigate', 'clinics')">
                Fix
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>

    <approve-doctor-modal v-if="approving" :doctor="approving" :cities="cities" @close="approving = null" @approved="onApproved" />
  </div>
</template>

<script>
import axios from "axios";
import ApproveDoctorModal from "./ApproveDoctorModal.vue";
import { errorMessage, initials } from "../admin-utils";

export default {
  name: "OverviewPanel",
  // The shell passes every panel the same props; ignore the ones this panel does not use.
  inheritAttrs: false,
  components: { ApproveDoctorModal },
  props: {
    stats: { type: Object, default: () => ({}) },
    statsLoaded: { type: Boolean, default: false },
    cities: { type: Array, default: () => [] },
  },
  emits: ["notify", "changed", "navigate"],
  data() {
    return {
      pending: [],
      pendingLoading: true,
      clinics: null,
      clinicUsers: null,
      admins: null,
      approving: null,
    };
  },
  computed: {
    cards() {
      const stat = key => (this.statsLoaded ? this.stats[key] || 0 : null);
      const count = list => (list === null ? null : list.length);
      return [
        { label: "Pending", value: stat("pending"), icon: "fas fa-hourglass-half", tone: "adm-tone-amber", panel: "doctors", intent: { status: "pending" } },
        { label: "Active doctors", value: stat("active"), icon: "fas fa-user-md", tone: "adm-tone-green", panel: "doctors", intent: { status: "active" } },
        { label: "All doctors", value: stat("total"), icon: "fas fa-users", tone: "adm-tone-blue", panel: "doctors", intent: { status: "all" } },
        { label: "Clinics", value: count(this.clinics), icon: "fas fa-hospital", tone: "adm-tone-teal", panel: "clinics" },
        { label: "Clinic logins", value: count(this.clinicUsers), icon: "fas fa-id-badge", tone: "adm-tone-violet", panel: "clinic-users" },
        { label: "Admins", value: count(this.admins), icon: "fas fa-user-shield", tone: "adm-tone-rose", panel: "admins" },
      ];
    },
    clinicsToSetUp() {
      return (this.clinics || []).filter(c => !c.doctor_count || !c.user_count);
    },
  },
  methods: {
    initials,
    async fetchPending() {
      this.pendingLoading = true;
      try {
        const { data } = await axios.get("/doctors", { params: { status: "pending", per_page: 5 } });
        this.pending = data.doctors || [];
      } catch (error) {
        this.pending = [];
        if (!error.response || error.response.status !== 404) {
          this.$emit("notify", errorMessage(error, "Could not load pending doctors."), "error");
        }
      } finally {
        this.pendingLoading = false;
      }
    },
    async fetchCounts() {
      const load = async (url, key) => {
        try {
          const { data } = await axios.get(url);
          this[key] = data || [];
        } catch (error) {
          this[key] = [];
        }
      };
      await Promise.all([
        load("/admin/clinics", "clinics"),
        load("/admin/clinic-users", "clinicUsers"),
        load("/admin/admins", "admins"),
      ]);
    },
    async onApproved(result) {
      const doctor = this.approving;
      this.approving = null;
      this.$emit("notify", `${doctor.doctorname} is approved. Login details were emailed to ${result.email}.`);
      this.$emit("changed");
      await Promise.all([this.fetchPending(), this.fetchCounts()]);
    },
  },
  created() {
    this.fetchPending();
    this.fetchCounts();
  },
};
</script>

<style scoped>
.adm-overview-grid {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(280px, 1fr);
  gap: 16px;
  align-items: start;
}

.adm-overview-side {
  display: grid;
  gap: 16px;
}

.adm-overview-person {
  flex: 1;
  min-width: 0;
}

.adm-quick {
  display: grid;
  gap: 8px;
}

.adm-quick .adm-btn {
  justify-content: flex-start;
}

.adm-quick .adm-btn i {
  width: 18px;
  color: var(--adm-muted);
}

.adm-overview-empty {
  padding: 24px 20px;
}

.adm-stat__skeleton {
  display: block;
  width: 48px;
  height: 26px;
}

@media (max-width: 1100px) {
  .adm-overview-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .adm-overview-review {
    display: none;
  }
}
</style>
